// src/lib/sw-update/store.ts

import {create} from 'zustand';

interface SwUpdateState {
  /**
   * True once a new build's service worker has installed and is waiting to
   * take over. Set from the `onNeedRefresh` callback in `main.tsx`.
   */
  updateAvailable: boolean;
  /**
   * True from the moment a handoff starts until the page is replaced.
   *
   * This is the *only* in-flight guard for taking a new build. All three
   * triggers — a route change, the banner, a 404'd chunk — go through
   * `takeNewBuild`, which reads and sets this, so two of them cannot start
   * competing handoffs. Keeping it here rather than in a module-level flag is
   * what lets the UI disarm its button off the same fact the logic branches on.
   */
  isApplying: boolean;

  markUpdateAvailable: () => void;
  markApplying: () => void;
}

/**
 * Whether a newer build is waiting. Deliberately NOT persisted — a pending
 * update belongs to this page session, and rehydrating a stale `true` would
 * show the banner on a tab that is already current.
 */
export const useSwUpdateStore = create<SwUpdateState>((set) => ({
  updateAvailable: false,
  isApplying: false,

  markUpdateAvailable: () => set({updateAvailable: true}),
  markApplying: () => set({isApplying: true}),
}));
